import { useState } from 'react';
import { finishSignIn, startSignIn } from '../lib/auth';

/**
 * Two steps, no passwords: type the email address your scheduler has on
 * file, then the six-digit code that arrives in that inbox.
 */
export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [session, setSession] = useState<string | null>(null);
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const r = await startSignIn(email);
      setSession(r.session);
      setDestination(r.destination);
    } catch (err) {
      setProblem((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    setBusy(true);
    setProblem(null);
    try {
      await finishSignIn(email, session, code);
      onSignedIn();
    } catch (err) {
      setProblem((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card login">
      <div className="loginbrand">
        <img src="/brand/cc-mark.svg" alt="" />
        <span className="wordmark">CallCovered</span>
        <span className="muted small">Always covered. Always reliable.</span>
      </div>
      <h1>Sign in</h1>
      {!session ? (
        <form onSubmit={sendCode}>
          <p className="muted">Use the email address your scheduler has for you. We'll send a code — no password to remember.</p>
          <label className="field">
            <span>Email</span>
            <input type="email" inputMode="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </label>
          {problem && <p className="error">{problem}</p>}
          <button type="submit" className="primary" disabled={busy || !email.includes('@')}>{busy ? 'Sending…' : 'Email me a code'}</button>
        </form>
      ) : (
        <form onSubmit={confirm}>
          <p className="muted">We sent a code to <strong>{destination}</strong>. It's good for 15 minutes.</p>
          {/* The codes come from Cognito's own sending account (EmailSendingAccount is
              COGNITO_DEFAULT), which is a generic no-reply address on a domain that is not
              ours and carries no reputation we can build — so it lands in spam often enough
              that "it never arrived" is really "I did not know where to look". Naming the
              sender is the part that helps: it is something to search for. (D103) */}
          <p className="muted small">
            Can't see it? Check your spam or junk folder — it arrives from{' '}
            <strong>no-reply@verificationemail.com</strong>.
          </p>
          <label className="field">
            <span>Code</span>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" pattern="[0-9 ]*" />
          </label>
          {problem && <p className="error">{problem}</p>}
          <div className="formactions">
            <button type="submit" className="primary" disabled={busy || code.trim().length < 4}>{busy ? 'Checking…' : 'Sign in'}</button>
            <button type="button" className="linklike" onClick={() => { setSession(null); setCode(''); setProblem(null); }}>use a different email</button>
          </div>
        </form>
      )}
    </section>
  );
}
